import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { loginApiEndPoint } from "../../apis/apis";
import Button from "../../components/Button";
import Footer from "../../components/Footer";
import RenderToast from "../../components/RenderToast";
import { BlackLogo } from "../../constant/imagePath";
import { LoginInitialValues } from "../../data/initial-values";
import { LoginSchema } from "../../schema/LoginSchema";
import {
  saveLoginUserData,
  setAllPermissions,
} from "../../store/auth/authSlice";
import classes from "./LoginPage.module.css";
import { fetchSiteConfiguration } from "@/store/site/configuration/siteConfigurationSlice";
import { usePost } from "@/apiHooks/commonMethods";
import queryClient from "@/config/queryClient";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import { cn } from "@/utils/cn";
import InputV2 from "@/components/InputV2/InputV2";
import { LuEye, LuEyeOff } from "react-icons/lu";
import UserIcon from "@/components/Icons/UserIcon";
import PasswordIcon from "@/components/Icons/PasswordIcon";

export default function LoginPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const loginFormik = useFormik({
    initialValues: LoginInitialValues,
    validationSchema: LoginSchema,
    onSubmit: (values) => {
      queryClient.clear();
      handleSubmit(values);
    },
  });

  const {
    mutate: login,
    data: loginData,
    isLoading,
    isSuccess,
  } = usePost({
    apiURL: loginApiEndPoint,
  });

  const handleSubmit = async (values) => {
    const data = { ...values, userType: "Admin" };
    login(data);
  };

  const handleSuccessLogin = () => {
    let allPermissions = loginData?.data?.user?.roles?.flatMap((role) =>
      role?.permissions.map((item) => item?.name)
    );
    dispatch(setAllPermissions(allPermissions));
    dispatch(saveLoginUserData(loginData?.data));
    dispatch(fetchSiteConfiguration()).unwrap();

    RenderToast({
      type: "success",
      message: `Login Successful.`,
    });
    navigate("/dashboard", { replace: true });
  };

  useEffect(() => {
    if (isSuccess) {
      handleSuccessLogin();
    }
  }, [isSuccess]);

  useFormikErrorToast(loginFormik);

  return (
    <div className="h-screen flex flex-col">
      <div className="h-full bg-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-y-8">
          <img alt="logo" src={BlackLogo} className={classes.logo} />
          <div className="w-[641px] shadow-[0_1px_3px_#00000040] gap-y-4 rounded-[10px] mb-[1px] p-4 flex flex-col border-[1px] border-disabled bg-white">
            <div className="text-[22px] text-center text-[#000E25] font-light leading-[22px]">
              Admin Login
            </div>
            <hr />
            <InputV2
              placeholder="Email"
              value={loginFormik?.values?.email}
              error={
                (loginFormik?.touched?.email && loginFormik?.errors?.email) ||
                (loginData?.status === 422 &&
                  loginData?.response?.data?.errors?.email?.join(" "))
              }
              onChange={(e) =>
                loginFormik?.setFieldValue("email", e.target.value)
              }
              startIcon={
                <div className="size-6">
                  <UserIcon />
                </div>
              }
              className="rounded-e-[6px]"
            />
            <InputV2
              placeholder="Password"
              type={showPassword ? "text" : "password"}
              value={loginFormik?.values?.password}
              onChange={(e) =>
                loginFormik?.setFieldValue("password", e.target.value)
              }
              error={
                (loginFormik?.touched?.password &&
                  loginFormik?.errors?.password) ||
                (loginData?.status === 422 &&
                  loginData?.response?.data?.errors?.password?.join(" "))
              }
              startIcon={
                <div className="size-6">
                  <PasswordIcon />
                </div>
              }
              endIcon={
                <div className="inline-flex items-center">
                  <button
                    onClick={() => setShowPassword(!showPassword)}
                    type="button"
                    className="w-6"
                  >
                    {showPassword ? <LuEyeOff /> : <LuEye />}
                  </button>
                  <div className="text-[14px] leading-[14px] font-light">
                    Forgot
                  </div>
                </div>
              }
              endIconClassName="border-s-0 w-auto px-2"
              containerClassName="w-full"
            />
            <Button
              disabled={isLoading}
              className={cn(
                classes.buttonDiv,
                "mt-8 !font-light underline-offset-1"
              )}
              type="submit"
              variant={"green-gradient"}
              label={"Log In"}
              onClick={loginFormik?.handleSubmit}
            />
            <hr />
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
